import { useState, useEffect } from "react";
import { IconX } from "@tabler/icons-react";
import { useForm, Controller } from "react-hook-form";
import toast from "react-hot-toast";
import { useShelfRowFloorCreateMutation } from "../../../hooks/shelveHooks";
import { useLanguage, T } from "../../../context/LanguageContext";

interface FloorFormData {
  floorName: string; // Real life: "FL", "BLK", "ZONE-A"
  floorNumbers: string; // Total physical spaces on the ground
  whAreaId: any;
  whRowId?: string; // Selected row for floor creation
  whRackId?: string;
  position?: string;
}

interface CreateFloorModalProps {
  shelveId: any;
  fetchShelves: () => void;
  rows?: any[]; // Available rows for selection
  selectedRow?: any; // Pre-selected row for floor creation
  onClose?: () => void; // Callback to close modal
  floorsByRack?: { [rackId: string]: { start: any[]; end: any[] } }; // Pre-computed rack floor occupancy
  floorsByRow?: { [rowId: string]: any[] }; // Pre-computed row floor occupancy
}

const CreateFloorModal: React.FC<CreateFloorModalProps> = ({
  shelveId,
  fetchShelves,
  rows = [],
  selectedRow,
  onClose,
  floorsByRack = {},
  floorsByRow = {},
}) => {
  const { t } = useLanguage();
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FloorFormData>({
    defaultValues: {
      floorName: "FL",
      floorNumbers: "1",
      whAreaId: shelveId,
      whRowId: selectedRow?.id || "",
      whRackId: "",
      position: "Start",
    },
  });

  const { mutate: createFloor, isPending } = useShelfRowFloorCreateMutation();

  const watchedPrefix = watch("floorName");
  const watchedCount = watch("floorNumbers");
  const watchedRowId = watch("whRowId");
  const watchedRackId = watch("whRackId");
  const currentSelectedRow = rows.find((r) => r.id === watchedRowId) || selectedRow;
  const allRacks = currentSelectedRow?.rack || [];

  const isRackStartBlocked = (rackId: string) => {
    if ((floorsByRack[rackId]?.start?.length ?? 0) > 0) return true;
    const rackIndex = allRacks.findIndex((r: any) => r.id === rackId);
    const rowFloors = currentSelectedRow ? (floorsByRow[currentSelectedRow.id] || []) : [];
    if (rackIndex === 0) {
      return rowFloors.length > 0;
    } else if (rackIndex > 0) {
      const prevRack = allRacks[rackIndex - 1];
      return (floorsByRack[prevRack.id]?.end?.length ?? 0) > 0;
    }
    return false;
  };

  const isRackEndBlocked = (rackId: string) => {
    if ((floorsByRack[rackId]?.end?.length ?? 0) > 0) return true;
    const rackIndex = allRacks.findIndex((r: any) => r.id === rackId);
    if (rackIndex !== -1 && rackIndex < allRacks.length - 1) {
      const nextRack = allRacks[rackIndex + 1];
      return (floorsByRack[nextRack.id]?.start?.length ?? 0) > 0;
    }
    return false;
  };

  const rowFloors = currentSelectedRow ? (floorsByRow[currentSelectedRow.id] || []) : [];
  const rowFloorBlocked = currentSelectedRow
    ? rowFloors.length > 0 ||
    (() => {
      const firstRack = allRacks[0];
      return firstRack ? (floorsByRack[firstRack.id]?.start?.length ?? 0) > 0 : false;
    })()
    : false;

  // Filter out racks where both Start and End positions are already blocked
  const availableRacks = allRacks.filter((rack: any) => {
    return !isRackStartBlocked(rack.id) || !isRackEndBlocked(rack.id);
  });

  const [preview, setPreview] = useState<string[]>([]);

  // Compute which positions are already occupied for the selected rack
  const rackStartOccupied = watchedRackId ? isRackStartBlocked(watchedRackId) : false;
  const rackEndOccupied = watchedRackId ? isRackEndBlocked(watchedRackId) : false;
  // Position is locked when exactly one of the two is occupied
  const positionLocked = (rackStartOccupied || rackEndOccupied) && !(rackStartOccupied && rackEndOccupied);
  const lockedPosition = rackStartOccupied ? "End" : "Start";

  // Auto-set position when rack selection changes
  useEffect(() => {
    if (!watchedRackId) {
      setValue("position", "");
      return;
    }
    if (rackStartOccupied && !rackEndOccupied) {
      setValue("position", "End");
    } else if (rackEndOccupied && !rackStartOccupied) {
      setValue("position", "Start");
    } else if (!rackStartOccupied && !rackEndOccupied) {
      setValue("position", "Start");
    }
  }, [watchedRackId, rackStartOccupied, rackEndOccupied, setValue]);

  useEffect(() => {
    const count = parseInt(watchedCount) || 0;
    const generated = Array.from(
      { length: count },
      (_, i) => `${watchedPrefix}-${i + 1}`,
    );
    setPreview(generated);
  }, [watchedPrefix, watchedCount]);

  const onSubmit = (data: FloorFormData) => {
    const payload = { ...data } as any;

    // If no row is selected, or if the selected row has no racks, clear whRackId and default position to Start
    if (!payload.whRowId || availableRacks.length === 0) {
      payload.whRackId = "";
      payload.position = "Start";
    }

    console.log("CreateFloorModal payload:", payload);
    createFloor(payload, {
      onSuccess: () => {
        toast.success(`${data.floorNumbers} ${t("Floor slots created")}`);
        reset();
        if (onClose) onClose();
        fetchShelves();
      },
      onError: (err: any) => toast.error(`Error: ${err.message}`),
    });
  };

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 p-2">
        <div className="grid grid-cols-2 gap-4">
          {/* Floor Name / Prefix */}
          <div className="space-y-1">
            <label className="text-sm font-semibold text-gray-700">
              <T>Floor Name</T>
            </label>
            <Controller
              name="floorName"
              control={control}
              rules={{
                required: "Required",
                minLength: 2,
                maxLength: { value: 3, message: "Max 3 characters" },
              }}
              render={({ field }) => (
                <input
                  {...field}
                  placeholder="e.g. FL"
                  maxLength={3}
                  className="w-full rounded-xl py-2.5 px-4 border border-gray-300 focus:ring-2 focus:ring-yellow-400 outline-none bg-white transition-all"
                />
              )}
            />
            {errors.floorName && (
              <p className="text-red-500 text-[10px]">
                {errors.floorName.message}
              </p>
            )}
          </div>

          {/* Slot Count */}
          <div className="space-y-1">
            <label className="text-sm font-semibold text-gray-700">
              <T>Number of Slots</T>
            </label>
            <Controller
              name="floorNumbers"
              control={control}
              render={({ field }) => (
                <select
                  {...field}
                  className="w-full rounded-xl py-2.5 px-4 border border-gray-300 focus:ring-2 focus:ring-yellow-400 outline-none bg-white transition-all"
                >
                  {[1, 2, 3, 4, 5, 10, 15, 20].map((n) => (
                    <option key={n} value={n}>
                      {n} {t("Slots")}
                    </option>
                  ))}
                </select>
              )}
            />
          </div>
        </div>

        {/* Row Selection */}
        <div className="space-y-1">
          <label className="text-sm font-semibold text-gray-700">
            {t("Select Row")} {selectedRow && `(${t("Selected:")}: ${selectedRow.name})`}
          </label>
          <Controller
            name="whRowId"
            control={control}
            render={({ field }) => (
              <select
                {...field}
                disabled={!!selectedRow}
                className="w-full rounded-xl py-2.5 px-4 border border-gray-300 focus:ring-2 focus:ring-yellow-400 outline-none bg-white transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">{t("Create floor without row association")}</option>
                {rows.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
              </select>
            )}
          />
          {/* <p className="text-xs text-gray-500">
            {selectedRow
              ? `Floor will be created after ${selectedRow.name}`
              : "Select a row if you want to create this floor after a specific row"}
          </p> */}
        </div>

        {/* Rack Selection */}
        {watchedRowId && availableRacks.length > 0 && (
          <div className="space-y-1">
            <label className="text-sm font-semibold text-gray-700">
              <T>Select Rack</T>
            </label>
            <Controller
              name="whRackId"
              control={control}
              rules={{
                validate: (val) => {
                  if (rowFloorBlocked && !val) {
                    return "Selecting a Rack is required because the Row Floor is already occupied/blocked.";
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <select
                  {...field}
                  className="w-full rounded-xl py-2.5 px-4 border border-gray-300 focus:ring-2 focus:ring-yellow-400 outline-none bg-white transition-all"
                >
                  <option value="">{t("Select Rack")}</option>
                  {availableRacks.map((rack: any) => (
                    <option key={rack.id} value={rack.id}>
                      {rack.name}
                    </option>
                  ))}
                </select>
              )}
            />
            {errors.whRackId && (
              <p className="text-red-500 text-[10px]">
                {errors.whRackId.message}
              </p>
            )}
            {rowFloorBlocked && (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-1">
                <T>Place the floor at start or end of the selected rack.</T>
              </p>
            )}
          </div>
        )}

        {/* Position Selection */}
        {watchedRowId && availableRacks.length > 0 && (
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-gray-700">
                <T>Position</T>
              </label>
              {positionLocked && (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                  {t("Auto-set")} · {t(lockedPosition)} {t("only")}
                </span>
              )}
            </div>
            <Controller
              name="position"
              control={control}
              render={({ field }) => (
                <select
                  {...field}
                  disabled={positionLocked || !watchedRackId}
                  className={`w-full rounded-xl py-2.5 px-4 border focus:ring-2 focus:ring-yellow-400 outline-none transition-all ${(positionLocked || !watchedRackId)
                    ? "bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed"
                    : "border-gray-300 bg-white"
                    }`}
                >
                  <option value="">{t("Select Position")}</option>
                  {["Start", "End"]
                    .filter((pos) => {
                      if (rackStartOccupied && pos === "Start") return false;
                      if (rackEndOccupied && pos === "End") return false;
                      return true;
                    })
                    .map((pos) => (
                      <option key={pos} value={pos}>
                        {t(pos)}
                      </option>
                    ))}
                </select>
              )}
            />
          </div>
        )}

        {/* Real-life Preview Section */}
        <div className="bg-white/50 border border-dashed border-gray-300 rounded-2xl p-4">
          <label className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">
            <T>Generating Location IDs</T>
          </label>
          <div className="mt-2 flex flex-wrap gap-2">
            {preview.map((id, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-mono font-bold rounded-lg border border-yellow-200"
              >
                {id}
              </span>
            ))}
          </div>
        </div>
      </form>

      {/* Footer */}
      <div className="py-2 px-4 border-t flex justify-end">
        <button
          type="submit"
          onClick={handleSubmit(onSubmit)}
          disabled={isPending}
          className="px-4 py-2 rounded-full bg-[#FBD037] text-black font-medium hover:bg-yellow-500 transition flex items-center justify-center"
        >
          {isPending ? t("Creating...") : t("Create")}
        </button>
      </div>
    </div>
    // </div>
  );
};

export default CreateFloorModal;
